import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  IconMessage,
  IconMessageCircle,
  IconRefresh,
  IconSparkles,
  IconX,
} from '@tabler/icons-react';
import { useContextStore } from '../hooks/context/useContextStore';
import { useIsMobile } from '../hooks/useIsMobile';
import AIChat from './AIChat';
import { subscribeToBottomComposers } from './ui/bottomComposerState';
import { subscribeToFullScreenLoaders } from './ui/fullScreenLoaderState';

type SupportView = 'closed' | 'selector' | 'ai';
const AVAILABILITY_TIMEOUT_MS = 5000;

/**
 * A detail view: one of the five record pages, which end in a chat card whose
 * composer sits at the very bottom of the page.
 */
const DETAIL_ROUTE = /^\/(jobs|estimates|appointments|invoices|service-providers)\/[^/]+$/;

export default function SupportWidget() {
  const { clientUser } = useContextStore();
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  const [view, setView] = useState<SupportView>('closed');
  // Minimised is not a fourth view: the panel keeps whichever view it was on,
  // so reopening from the button puts the client back where they were instead
  // of at the menu. This is how the SP app holds it too.
  const [minimized, setMinimized] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCrispChatOpen, setIsCrispChatOpen] = useState(false);
  const [hasBottomComposer, setHasBottomComposer] = useState(false);

  useEffect(() => {
    const resolveInitialAvailability = () => {
      const isAvailable = (window.$crisp as unknown as { is?: (method: string) => boolean })?.is?.(
        'website:available'
      );
      if (typeof isAvailable === 'boolean') setAvailable(isAvailable);
    };
    const previousReadyTrigger = window.CRISP_READY_TRIGGER;
    const handleCrispReady = () => {
      previousReadyTrigger?.();
      resolveInitialAvailability();
    };
    const timeout = window.setTimeout(() => setAvailable(false), AVAILABILITY_TIMEOUT_MS);

    window.$crisp = window.$crisp ?? [];
    window.$crisp.push(['do', 'chat:hide']);
    window.$crisp.push(['on', 'website:availability:changed', setAvailable]);
    window.CRISP_READY_TRIGGER = handleCrispReady;
    window.$crisp.push([
      'on',
      'chat:closed',
      () => {
        window.$crisp?.push(['do', 'chat:hide']);
        setIsCrispChatOpen(false);
      },
    ]);

    return () => {
      window.$crisp?.push(['off', 'website:availability:changed']);
      window.$crisp?.push(['off', 'chat:closed']);
      window.clearTimeout(timeout);
      if (window.CRISP_READY_TRIGGER === handleCrispReady) {
        if (previousReadyTrigger) {
          window.CRISP_READY_TRIGGER = previousReadyTrigger;
        } else {
          delete window.CRISP_READY_TRIGGER;
        }
      }
    };
  }, []);

  useEffect(() => subscribeToBottomComposers(count => setHasBottomComposer(count > 0)), []);

  useEffect(
    () =>
      subscribeToFullScreenLoaders(activeLoaderCount => {
        setIsLoading(activeLoaderCount > 0);
        window.$crisp = window.$crisp ?? [];
        window.$crisp.push(['do', 'chat:hide']);
      }),
    []
  );

  if (!clientUser || isLoading) return null;

  /**
   * A phone reading a conversation has nothing else on screen: the thread
   * fills it and the composer owns the bottom edge. SP drops the button
   * outright there (`SupportFAB`, `isInConversationView ? 'md:flex hidden'`)
   * and that is the right call — lifting it only moves it onto the messages.
   * From `md` up the columns leave room, so it stays.
   */
  if (isMobile && hasBottomComposer && pathname.startsWith('/inbox')) return null;

  const availabilityLabel =
    available === true ? 'Online' : available === false ? 'Offline' : 'Checking availability';
  const availabilityClass =
    available === true
      ? 'bg-emerald-50 text-emerald-700'
      : 'bg-ds-bg-secondary text-ds-text-secondary';

  // On a phone a detail view ends in the chat card, whose composer sits at the
  // bottom of the page — and the send button is in the bottom-right corner,
  // exactly where this button floats. That one is a width question, so it is
  // asked that way.
  //
  // A composer that registers through `bottomComposerState` is not: it is
  // saying its send button is in that corner, which is as true on a tablet as
  // on a phone. Gating it on the width left the tablet inbox's send button
  // underneath this one. Everywhere else, and wherever the chat is a sidebar
  // card rather than the last thing on the page, the button keeps its corner.
  const clearsChatComposer = hasBottomComposer || (isMobile && DETAIL_ROUTE.test(pathname));
  const fabBottom = clearsChatComposer ? 'bottom-24' : 'bottom-5';
  const panelBottom = clearsChatComposer ? 'bottom-44' : 'bottom-24';

  const handleBack = () => setView('selector');
  const handleMinimize = () => setMinimized(true);
  const handleClose = () => {
    setView('closed');
    setMinimized(false);
  };

  // Minimised → put back whatever was open; otherwise start at the menu. There
  // is no third case: unlike the SP app's, this button is not drawn while a
  // panel is up, so it never has to stand in for the panel's own close.
  const handleFabClick = () => (minimized ? setMinimized(false) : setView('selector'));

  return (
    <div data-support-widget>
      {(view === 'closed' || minimized) && !isCrispChatOpen && (
        <button
          type="button"
          aria-label="AI assistant and support"
          onClick={handleFabClick}
          className={`fixed ${fabBottom} right-5 z-[9999] flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-lg transition-transform hover:scale-110`}
        >
          <IconMessageCircle size={26} stroke={1.75} />
        </button>
      )}
      {view === 'selector' && !minimized && (
        <div
          className={`fixed ${panelBottom} right-5 z-[9998] w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-ds-border bg-white shadow-2xl`}
        >
          <header className="flex items-center justify-between border-b border-ds-border px-5 py-3">
            <span className="text-sm font-semibold">AI assistant &amp; support</span>
            <button
              type="button"
              aria-label="Close support"
              onClick={handleClose}
              className="rounded-full p-1 hover:bg-ds-bg-secondary"
            >
              <IconX size={18} />
            </button>
          </header>
          <div className="space-y-2 p-4">
            <button
              type="button"
              onClick={() => setView('ai')}
              className="flex w-full gap-3 rounded-xl border border-ds-border p-4 text-left hover:bg-primary/5"
            >
              <IconSparkles className="text-primary" size={22} />
              <span>
                <span className="block text-sm font-semibold">AI assistant</span>
                <span className="text-xs text-ds-text-tertiary">
                  Ask DHS AI for help with your account.
                </span>
              </span>
            </button>
            <button
              type="button"
              aria-label="Chat with support"
              onClick={() => {
                setView('closed');
                setIsCrispChatOpen(true);
                window.$crisp?.push(['do', 'chat:show']);
                window.$crisp?.push(['do', 'chat:open']);
              }}
              className="flex w-full gap-3 rounded-xl border border-ds-border p-4 text-left hover:bg-primary/5"
            >
              <IconMessage className="text-pink-600" size={22} />
              <span>
                <span className="block text-sm font-semibold">Chat with support</span>
                <span
                  className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${availabilityClass}`}
                >
                  {available === null ? (
                    <IconRefresh
                      data-testid="support-availability-checking"
                      size={13}
                      className="animate-spin"
                    />
                  ) : (
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${available ? 'bg-emerald-500' : 'bg-red-500'}`}
                    />
                  )}
                  {availabilityLabel}
                </span>
              </span>
            </button>
          </div>
        </div>
      )}
      {view === 'ai' && !minimized && (
        <div className={`fixed ${panelBottom} right-5 z-[9998]`}>
          <AIChat embedded onBack={handleBack} onMinimize={handleMinimize} onClose={handleClose} />
        </div>
      )}
    </div>
  );
}
